import { Component, DestroyRef, DoCheck, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { skip, take } from 'rxjs';
import { CampaignMessagesReportTableComponent } from '../../../../../../components/tables/campaign-messages-report-table/campaign-messages-report-table.component';
import { CampaignRecipientsReportTableComponent } from '../../../../../../components/tables/campaign-recipients-report-table/campaign-recipients-report-table.component';
import { EngagementBreakdownByMessagesGraphComponent } from '../../../../../../components/organization/campaigns/report-graphs/engagement-breakdown-by-messages-graph/engagement-breakdown-by-messages-graph.component';
import { GenderDistributionGraphComponent } from '../../../../../../components/organization/campaigns/report-graphs/gender-distribution-graph/gender-distribution-graph.component';
import { DeliveryRateByRecipientsGraphComponent } from '../../../../../../components/organization/campaigns/report-graphs/delivery-rate-by-recipients-graph/delivery-rate-by-recipients-graph.component';
import { AgeGroupDistributionGraphComponent } from '../../../../../../components/organization/campaigns/report-graphs/age-group-distribution-graph/age-group-distribution-graph.component';
import {
  CampaignReportType,
  CampaignsService,
} from '../../../../../../services/campaign/campaigns-api.service';
import { CampaignDetails } from '../../../types/campaignDetails';

/**
 * Campaign report: every send with its delivery rate, delivery by message and
 * by group, and who the campaign reached by gender and age.
 */
@Component({
  selector: 'app-report-overview',
  standalone: true,
  imports: [
    CampaignMessagesReportTableComponent,
    CampaignRecipientsReportTableComponent,
    EngagementBreakdownByMessagesGraphComponent,
    GenderDistributionGraphComponent,
    DeliveryRateByRecipientsGraphComponent,
    AgeGroupDistributionGraphComponent,
  ],
  templateUrl: './report-overview.component.html',
  styleUrl: './report-overview.component.css',
})
export class ReportOverviewComponent implements OnInit, DoCheck {
  private destroyRef = inject(DestroyRef);
  campaignReport: CampaignReportType = {
    messagesReport: [],
    recipientsReport: [],
    messagesGraphs: null,
    recipientsGraphs: null,
  };
  campaignsService = inject(CampaignsService);
  campaign!: CampaignDetails;
  route: ActivatedRoute = inject(ActivatedRoute);

  state: 'loading' | 'ready' | 'error' = 'loading';
  private fetchedUuid = '';

  ngOnInit(): void {
    // get campaign details so you can use the campaign id to fetch reports
    this.campaignsService.getCampaignDetails
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((campaign) => {
        this.campaign = campaign as CampaignDetails;

        // fetching reports, once per campaign (not on every details refresh)
        if (this.campaign.uuid && this.campaign.uuid !== this.fetchedUuid) {
          this.fetch(this.campaign.uuid);
        }
      });

    // subscribing to reports and assigning to value
    this.campaignsService.getCampaignReport
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((report) => {
        this.campaignReport = report;
      });
  }

  private fetch(uuid: string) {
    this.fetchedUuid = uuid;
    this.state = 'loading';
    // Clear the service's error first: any error text seen after this, even
    // the same words as a moment ago, belongs to this request.
    this.campaignsService.errorMsg = '';
    // The next report emitted after this request is this campaign's.
    this.campaignsService.getCampaignReport
      .pipe(skip(1), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.state = 'ready'));
    this.campaignsService.fetchCampaignReport(uuid);
  }

  retry() {
    if (this.campaign?.uuid) this.fetch(this.campaign.uuid);
  }

  /** The report request signals failure only through the service's error flags. */
  ngDoCheck() {
    if (
      this.state === 'loading' &&
      !this.campaignsService.isFetching &&
      !!this.campaignsService.errorMsg
    ) {
      this.state = 'error';
    }
  }
}
